import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface Product {
    productId: number
    productName: string
    price: any
    discountPrice?: number
    thumbnailUrl: string
    brandName?: string
    keyword?: string
    badge?: string // 💡 배지 타입 추가
}

export default function NewProductSlider({
                                             title = "신규 등록 상품",
                                             subtitle = "방금 도착한 따끈따끈한 신상품을 만나보세요.",
                                             products
                                         }: {
    title?: string
    subtitle?: string
    products: Product[]
}) {
    const navigate = useNavigate()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [showLeftBtn, setShowLeftBtn] = useState(false)
    const [showRightBtn, setShowRightBtn] = useState(true)

    const formatPrice = (price: number) => price.toLocaleString() + "원"

    const scroll = (direction: "left" | "right") => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current
            const scrollTo = direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth
            scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" })
        }
    }

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
            setShowLeftBtn(scrollLeft > 0)
            setShowRightBtn(scrollLeft < scrollWidth - clientWidth - 10)
        }
    }

    useEffect(() => {
        handleScroll()
    }, [products])

    return (
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-6">
          <div className="mb-8 flex items-end justify-between">
              <div>
                  <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
                  {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
              </div>
              <div className="flex gap-2">
                  <button
                    onClick={() => scroll("left")}
                    disabled={!showLeftBtn}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-all hover:bg-slate-50 disabled:opacity-30"
                  >
                      <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => scroll("right")}
                    disabled={!showRightBtn}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-all hover:bg-slate-50 disabled:opacity-30"
                  >
                      <ChevronRight size={20} />
                  </button>
              </div>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="no-scrollbar flex gap-6 overflow-x-auto scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
              {products.map((product) => (
                <div
                  key={product.productId}
                  className="min-w-[280px] w-[280px] cursor-pointer group"
                  onClick={() => navigate(`/product/${product.productId}`)}
                >
                    <div className="relative aspect-square overflow-hidden rounded-[32px] bg-slate-100">
                        <img
                          src={product.thumbnailUrl}
                          alt={product.productName}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />

                        {/* 💡 좌측 상단 배지 노출 영역 추가 */}
                        <div className="absolute left-4 top-4 flex flex-col gap-1.5 z-10">
                            {product.badge && (
                              <span className="w-fit rounded-full bg-rose-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                                        {product.badge}
                                    </span>
                            )}
                            {product.discountPrice && (
                              <span className="w-fit rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white shadow-sm">
                                        SAVE
                                    </span>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 px-2">
                        <p className="text-xs font-medium text-slate-400">{product.brandName}</p>
                        <h3 className="mt-1 truncate text-lg font-bold text-slate-900">{product.productName}</h3>
                        <div className="mt-2 flex items-center gap-2">
                                <span className="text-xl font-black text-slate-900">
                                    {formatPrice(product.discountPrice || product.price)}
                                </span>
                            {product.discountPrice && (
                              <span className="text-sm text-slate-400 line-through">
                                        {formatPrice(product.price)}
                                    </span>
                            )}
                        </div>
                    </div>
                </div>
              ))}
          </div>
      </section>
    )
}